/**
 * Localization Modal Web Component
 * Handles country and language selection modal functionality
 */

class LocalizationModal extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.overlay = null;
    this.container = null;
    this.closeButton = null;
    this.form = null;
    this.isOpen = false;

    this.init();
  }

  init() {
    this.modal = this;
    this.overlay = this.querySelector('[data-localization-modal-overlay]');
    this.container = this.querySelector('[data-localization-modal-container]');
    this.closeButton = this.querySelector('[data-localization-modal-close]');
    this.form = this.querySelector('.localization-modal__form');

    this.bindEvents();
  }

  bindEvents() {
    // Close modal events
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key to close modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Prevent modal container clicks from closing modal
    if (this.container) {
      this.container.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    // Form submission handling
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }

    // Trap focus within modal when open
    this.addEventListener('keydown', (e) => this.handleKeyDown(e));
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Focus management
    this.trapFocus();

    // Trigger custom event
    this.dispatchEvent(new CustomEvent('localization-modal:opened', {
      bubbles: true,
      detail: { modal: this }
    }));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';

    // Return focus to trigger element
    const trigger = document.querySelector('[data-localization-modal-trigger]');
    if (trigger) {
      trigger.focus();
    }

    // Trigger custom event
    this.dispatchEvent(new CustomEvent('localization-modal:closed', {
      bubbles: true,
      detail: { modal: this }
    }));
  }

  handleFormSubmit(e) {
    // Optionally prevent default and handle with AJAX
    // For now, let the form submit normally to Shopify's localization endpoint
    const continueButton = this.form.querySelector('.localization-modal__continue');
    if (continueButton) {
      continueButton.disabled = true;
      continueButton.textContent = 'Loading...';
    }

    // Store selection in localStorage for better UX
    const countrySelect = this.form.querySelector('[data-localization-country]');
    const languageSelect = this.form.querySelector('[data-localization-language]');

    if (countrySelect && languageSelect) {
      const selection = {
        country: countrySelect.value,
        language: languageSelect.value,
        timestamp: Date.now()
      };
      localStorage.setItem('localization-selection', JSON.stringify(selection));
    }

    // Trigger custom event
    this.dispatchEvent(new CustomEvent('localization-modal:submitted', {
      bubbles: true,
      detail: {
        modal: this,
        country: countrySelect?.value,
        language: languageSelect?.value
      }
    }));
  }

  handleKeyDown(e) {
    if (!this.isOpen) return;

    // Tab key handling for focus trap
    if (e.key === 'Tab') {
      this.handleTabKey(e);
    }
  }

  handleTabKey(e) {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  // Public API methods
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  // Static method to get current modal instance
  static getInstance() {
    return document.querySelector('localization-modal');
  }
}

// Register the custom element
if (!customElements.get('localization-modal')) {
  customElements.define('localization-modal', LocalizationModal);
}

// Global functions for easy access
window.openLocalizationModal = function() {
  const modal = LocalizationModal.getInstance();
  if (modal) {
    modal.open();
  }
};

window.closeLocalizationModal = function() {
  const modal = LocalizationModal.getInstance();
  if (modal) {
    modal.close();
  }
};

window.toggleLocalizationModal = function() {
  const modal = LocalizationModal.getInstance();
  if (modal) {
    modal.toggle();
  }
};

// Initialize trigger buttons
document.addEventListener('DOMContentLoaded', function() {
  // Set up trigger buttons
  const triggers = document.querySelectorAll('[data-localization-modal-trigger]');
  triggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      window.openLocalizationModal();
    });
  });

  // Check if user has previously made a selection
  const savedSelection = localStorage.getItem('localization-selection');
  if (savedSelection) {
    try {
      const selection = JSON.parse(savedSelection);
      const daysSince = (Date.now() - selection.timestamp) / (1000 * 60 * 60 * 24);

      // If selection is older than 30 days, show modal again
      if (daysSince > 30) {
        localStorage.removeItem('localization-selection');
      }
    } catch (e) {
      localStorage.removeItem('localization-selection');
    }
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = LocalizationModal;
}